// =============================================================================
// MeuDriver — Hook: useCalendarExport (Batch T3)
// Exportação client-side de aulas agendadas para formato iCalendar (RFC 5545)
// =============================================================================

import { useCallback } from 'react'
import { useStudentBookings, useInstructorBookings } from '@/hooks/useBookings'
import type { BookingRow } from '@/types/database'

// ─── Helpers iCal ─────────────────────────────────────────────────────────────

/** Formata Date para o formato iCal: YYYYMMDDTHHMMSSZ */
function toIcalDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
}

/** Escapa valores de texto conforme RFC 5545 §3.3.11 */
function escapeIcalText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
}

/** Quebra linha longa em múltiplas linhas de 75 octetos (RFC 5545 §3.1) */
function foldLine(line: string): string {
  const LIMIT = 75
  if (line.length <= LIMIT) return line
  let result = ''
  let pos = 0
  result += line.slice(pos, LIMIT)
  pos = LIMIT
  while (pos < line.length) {
    result += '\r\n ' + line.slice(pos, pos + LIMIT - 1)
    pos += LIMIT - 1
  }
  return result
}

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface BookingForExport {
  id: string
  scheduled_at: string
  duration_minutes: number
  pickup_address?: string | null
  /** Nome do outro participante (instrutor ou aluno) */
  otherPartyName?: string | null
  /** Tipo de aula como texto legível */
  lessonTypeLabel?: string
}

// ─── Geração do .ics ──────────────────────────────────────────────────────────

function buildIcsContent(bookings: BookingForExport[]): string {
  const now = toIcalDate(new Date())

  const events = bookings
    .map((b) => {
      const start = new Date(b.scheduled_at)
      const durationMs = (b.duration_minutes ?? 60) * 60 * 1000
      const end = new Date(start.getTime() + durationMs)

      const summary = escapeIcalText(
        [b.lessonTypeLabel ?? 'Aula de direção', b.otherPartyName].filter(Boolean).join(' — '),
      )

      const description = b.pickup_address
        ? escapeIcalText(`Endereço de partida: ${b.pickup_address}`)
        : ''

      const uid = `${b.id}@meudriver.com.br`

      const lines = [
        'BEGIN:VEVENT',
        foldLine(`UID:${uid}`),
        foldLine(`DTSTAMP:${now}`),
        foldLine(`DTSTART:${toIcalDate(start)}`),
        foldLine(`DTEND:${toIcalDate(end)}`),
        foldLine(`SUMMARY:${summary}`),
      ]

      if (description) {
        lines.push(foldLine(`DESCRIPTION:${description}`))
      }

      lines.push('END:VEVENT')
      return lines.join('\r\n')
    })
    .join('\r\n')

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MeuDriver//MeuDriver//PT',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    events,
    'END:VCALENDAR',
  ].join('\r\n')
}

// ─── downloadIcs ──────────────────────────────────────────────────────────────

export function downloadIcs(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── useExportToIcs ───────────────────────────────────────────────────────────

export function useExportToIcs() {
  const exportFn = useCallback(
    (bookings: BookingForExport[], filename = 'meudriver-aulas') => {
      if (bookings.length === 0) return
      const content = buildIcsContent(bookings)
      downloadIcs(content, filename)
    },
    [],
  )

  return exportFn
}

// ─── useExportStudentBookings ─────────────────────────────────────────────────

export function useExportStudentBookings() {
  const { data: bookings = [], isLoading } = useStudentBookings()
  const exportToIcs = useExportToIcs()

  const handleExport = useCallback(() => {
    const mapped: BookingForExport[] = bookings.map((b) => {
      const raw = b as BookingRow & {
        instructor?: {
          profile?: { full_name?: string | null } | null
        } | null
      }

      return {
        id:              b.id,
        scheduled_at:   b.scheduled_at,
        duration_minutes: b.duration_minutes ?? 60,
        pickup_address:  b.pickup_address ?? null,
        otherPartyName:  raw.instructor?.profile?.full_name ?? null,
        lessonTypeLabel: 'Aula de direção',
      }
    })

    exportToIcs(mapped, 'meudriver-aulas-aluno')
  }, [bookings, exportToIcs])

  return { handleExport, isLoading, count: bookings.length }
}

// ─── useExportInstructorBookings ──────────────────────────────────────────────

export function useExportInstructorBookings() {
  const { data: bookings = [], isLoading } = useInstructorBookings()
  const exportToIcs = useExportToIcs()

  const handleExport = useCallback(() => {
    const mapped: BookingForExport[] = bookings.map((b) => {
      const raw = b as BookingRow & {
        student?: { full_name?: string | null } | null
      }

      return {
        id:              b.id,
        scheduled_at:   b.scheduled_at,
        duration_minutes: b.duration_minutes ?? 60,
        pickup_address:  b.pickup_address ?? null,
        otherPartyName:  raw.student?.full_name ?? null,
        lessonTypeLabel: 'Aula de direção',
      }
    })

    exportToIcs(mapped, 'meudriver-aulas-instrutor')
  }, [bookings, exportToIcs])

  return { handleExport, isLoading, count: bookings.length }
}
